import { Box, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { ICandidateSubmissionSearchFilterVM } from 'wisestep-api';
import {
  WsSelect,
  WsSelectObj,
  WsSmartTagList,
  WsTypoColors,
  WsTypoVariants,
  WsTypography,
} from 'wisestep-base';

import AccountManagerUserFilter from '../../../../../components/filters/components/AccountManagerFilter';
import { extractEmail } from '../../../../../utils/StringUtils';
import { getBusinessDefination, getFilterArray, jobStatusOptions } from '../../../utils/Helper';
import useGetBusinessDefination from '../../hooks/useGetBusinessDefination';

import SmartFilter from './SmartFilter';

export interface ISmartFilterList {
  filterName: string;
  values: any;
}

interface WsFilterTagsProps {
  searchQuery: ICandidateSubmissionSearchFilterVM;
  onDelete?: (chip: string) => void;
  label?: string;
  getUpdatedFiltersFromAppliedFilters: (data) => void;
}

const ApplyedFilter = ({
  searchQuery,
  onDelete,
  label = 'Applied Filter:',
  getUpdatedFiltersFromAppliedFilters,
}: WsFilterTagsProps) => {
  const [appliedFilters, setAppliedFilters] = useState<ISmartFilterList[]>([
    { filterName: '', values: '' },
  ]);
  const { businessDefinations } = useGetBusinessDefination();
  const methods = useForm();
  const businessDefinationOptions: WsSelectObj[] = [
    { label: 'None', value: 'None' },
    ...businessDefinations.map((defination) => ({
      label: defination.businessDefinitionName,
      value: defination.id,
    })),
  ];

  useEffect(() => {
    const filterNameArray = getFilterArray(searchQuery);

    const filterResults = searchQuery.filters.reduce(
      (acc, filter) => {
        if (filter?.fields?.length > 0) {
          filter?.fields?.forEach((field) => {
            acc.stages.add(field['applicationStage']);
            acc.states.add(field['applicationState']);
          });
        }
        return acc;
      },
      { stages: new Set(), states: new Set() }
    );
    const filterApplicationStage1 = {
      filterName: 'APPLICATION_STAGE',
      values: [Array.from(filterResults.stages).join(', ')],
    };

    const filterApplicationStatus1 = {
      filterName: 'APPLICATION_STATUS',
      values: [Array.from(filterResults.states).join(', ')],
    };

    const filterApplicationStageContainsEmptyString = filterApplicationStage1.values.some(
      (value) => value === ''
    );
    const filterApplicationStatusContainsEmptyString = filterApplicationStatus1.values.some(
      (value) => value === ''
    );

    !filterApplicationStageContainsEmptyString &&
      filterResults.stages.size > 0 &&
      filterNameArray.push(filterApplicationStage1);
    !filterApplicationStatusContainsEmptyString &&
      filterResults.states.size > 0 &&
      filterNameArray.push(filterApplicationStatus1);

    setAppliedFilters(filterNameArray);
  }, [searchQuery]);

  useEffect(() => {
    if (searchQuery.filters.length == 0 && !searchQuery.businessDefinitionId) {
      methods.reset();
    }
  }, [methods, searchQuery]);

  const updatedQuery = { ...searchQuery };

  return (
    <FormProvider {...methods}>
      <Grid container item gap={1} alignItems="center" pt={1}>
        <Grid>
          <WsTypography variant={WsTypoVariants.SUB_TITLE} bold color={WsTypoColors.BLACK}>
            {label}
          </WsTypography>
        </Grid>

        <Grid>
          <SmartFilter
            filterName="Account Manager :"
            filterValue={
              methods.watch('accountMangers')?.length > 0
                ? `Selected ${methods.watch('accountMangers').length}`
                : `All`
            }
            onClick={() => {
              const updatedQuery1 = updatedQuery.filters.filter(
                (filter) => filter.filterName !== 'ACCOUNT_MANAGER'
              );
              if (methods.watch('accountMangers').length > 0)
                updatedQuery1.push({
                  filterName: 'ACCOUNT_MANAGER',
                  values:
                    methods.watch('accountMangers')?.map((email) => extractEmail(email)) ?? [],
                  fields: {},
                });
              updatedQuery.filters = updatedQuery1;
              getUpdatedFiltersFromAppliedFilters(updatedQuery);
            }}
          >
            <Box width="100%">
              <AccountManagerUserFilter
                name="accountMangers"
                multiple={true}
                isBorderStyle={true}
              />
            </Box>
          </SmartFilter>
        </Grid>
        <Grid>
          <SmartFilter
            filterName="Job Status :"
            filterValue={methods.watch('jobStatus') ?? 'All'}
            onClick={() => {
              const updatedQuery1 = updatedQuery.filters.filter(
                (filter) => filter.filterName !== 'JOB_STATUS'
              );
              updatedQuery1.push({
                filterName: 'JOB_STATUS',
                values:
                  methods.watch('jobStatus') == 'All'
                    ? [true, false]
                    : methods.watch('jobStatus') == 'Opened'
                    ? [false]
                    : [true],
                fields: {},
              });
              updatedQuery.filters = updatedQuery1;
              getUpdatedFiltersFromAppliedFilters(updatedQuery);
            }}
          >
            <Box width="100%">
              <WsSelect name="jobStatus" selectArray={jobStatusOptions} placeholder="Select" />
            </Box>
          </SmartFilter>
        </Grid>
        <Grid>
          <SmartFilter
            filterName="Business Definition :"
            filterValue={
              getBusinessDefination(
                methods.watch('businessDefination') ?? searchQuery.businessDefinitionId ?? 'None',
                businessDefinationOptions
              )?.label ?? 'None'
            }
            onClick={() => {
              const updatedQuery1 = { ...updatedQuery };
              updatedQuery1.businessDefinitionId = undefined;
              if (methods.watch('businessDefination') !== 'None') {
                updatedQuery1.businessDefinitionId = methods.watch('businessDefination');
              }
              getUpdatedFiltersFromAppliedFilters(updatedQuery1);
            }}
          >
            <Box width="100%">
              <WsSelect
                name="businessDefination"
                selectArray={businessDefinationOptions}
                placeholder="Select"
              />
            </Box>
          </SmartFilter>
        </Grid>
        {appliedFilters.length > 0 && (
          <Grid container direction="row">
            {appliedFilters
              .filter(
                (filter) =>
                  [
                    'JOB_STATUS',
                    'ACCOUNT_MANAGER',
                    'STAGE_STATUS',
                    'CLIENT_ID',
                    'CLIENT_CONTACT_ID',
                    'JOB_ID',
                    'JOINING_BETWEEN',
                  ].includes(filter.filterName) == false
              )
              .map((filteredFilter) =>
                Array.isArray(filteredFilter.values) ? (
                  <Grid item key={filteredFilter.filterName}>
                    <WsSmartTagList
                      chipArray={filteredFilter.values.map(
                        (value) => `${filteredFilter.filterName} : ${value}`
                      )}
                      count={100}
                      allowDelete={true}
                      onDelete={onDelete}
                    />
                  </Grid>
                ) : null
              )}
          </Grid>
        )}
      </Grid>
    </FormProvider>
  );
};
export default ApplyedFilter;
